/*
  Old School: shop mega menu behaviour (header block "Old School mega menu", type os_mega_menu).
  Markup: snippets/os-mega-menu.liquid (desktop) and snippets/os-mega-menu-mobile.liquid (mobile).
  Built for the SA theme (Oct 2026), ported unchanged to Old School Global (Nov 2026). One-tab mode
  needs nothing extra here: it is one tab and one panel.

  - Rail items / tabs ([data-os-mm-tab]) switch panels on click and keyboard; on desktop also on hover.
    On desktop the panel area glides to the new tab's height instead of snapping.
  - Sport chips ([data-os-mm-chip]) filter the tiles in their panel by data-os-sport, which lists every
    sport a team is in, space separated (a team can be in more than one).
  - Past 14 tiles the "More teams" tile ([data-os-mm-more]) reveals the tiles past the 13th. (A desktop
    panel with more than 10 tiles goes 7 across; the snippet decides that once, in data-os-mm-wide.)
  - Campaign dots ([data-os-mm-dot]) switch the campaign card.
  - Menu images are lazy; they start loading as soon as the shopper reaches the menu, so a tab never
    opens on empty grey tiles.
  - Mobile: the search and tabs ([data-os-mm-top]) stick under Purity's back header; its height is kept
    in --os-mm-sticky-top. Switching tabs while scrolled down starts the new panel at its top. The tab
    and chip rows fade at the edge that has more to show.
  The server renders the first panel and first campaign visible, so the menu still works without this file.
*/
(() => {
  if (!window.customElements || window.customElements.get('os-mega-menu')) return;

  const HOVER_DELAY = 90;
  const RESIZE_MS = 280;
  const reducedMotion = () =>
    !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  // Lazy images in a display:none panel never load, so switch them to eager before they are needed
  const warmImages = (root) => {
    root.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });
  };

  class OsMegaMenu extends HTMLElement {
    connectedCallback() {
      window.requestAnimationFrame(() => this.markWide());
      if (this.dataset.variant === 'mobile') {
        this.observeHeader();
        this.watchRows();
        this.watchScroll();
      }
      if (this.osReady) return;
      this.osReady = true;
      this.hoverTimer = null;
      this.resizeTimer = null;
      this.tabs = Array.from(this.querySelectorAll('[data-os-mm-tab]'));
      const hoverable = this.dataset.variant === 'desktop';

      // Old School (Global): a chip picked last time does not stay picked when the panel reopens
      const parentDetails = hoverable ? this.closest('details') : null;
      if (parentDetails) {
        parentDetails.addEventListener('toggle', () => {
          if (parentDetails.open) return;
          this.querySelectorAll('[data-os-mm-chips]').forEach((group) => {
            const all = group.querySelector('[data-os-mm-chip="all"]');
            if (all && all.getAttribute('aria-pressed') !== 'true') this.filter(all);
          });
        });
      }

      let warmed = false;
      const warm = () => {
        if (warmed) return;
        warmed = true;
        warmImages(this);
      };
      ['pointerenter', 'focusin', 'touchstart'].forEach((type) => {
        this.addEventListener(type, warm, { once: true, passive: true });
      });

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.select(tab));
        tab.addEventListener('keydown', (event) => this.onTabKeydown(event, tab));
        if (hoverable) {
          tab.addEventListener('mouseenter', () => {
            clearTimeout(this.hoverTimer);
            this.hoverTimer = setTimeout(() => this.select(tab), HOVER_DELAY);
          });
          tab.addEventListener('mouseleave', () => clearTimeout(this.hoverTimer));
        }
      });

      this.addEventListener('click', (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;
        const chip = target.closest('[data-os-mm-chip]');
        if (chip && this.contains(chip)) {
          this.filter(chip);
          return;
        }
        const more = target.closest('[data-os-mm-more]');
        if (more && this.contains(more)) {
          this.expand(more);
          return;
        }
        const dot = target.closest('[data-os-mm-dot]');
        if (dot && this.contains(dot)) this.showSlide(dot);
      });
    }

    disconnectedCallback() {
      clearTimeout(this.hoverTimer);
      clearTimeout(this.resizeTimer);
      if (this.headerObserver) {
        this.headerObserver.disconnect();
        this.headerObserver = null;
      }
      if (this.rowObserver) {
        this.rowObserver.disconnect();
        this.rowObserver = null;
      }
      if (this.scroller && this.onScroll) {
        this.scroller.removeEventListener('scroll', this.onScroll);
        this.scroller = null;
      }
    }

    // Mobile: keep --os-mm-sticky-top equal to the height of the pinned bar (.submenu-header, rendered
    // just before this element), so the search and tabs pin right under it.
    // The drawer is hidden until opened, so the height is measured again whenever it changes.
    observeHeader() {
      if (this.headerObserver) return;
      let header = this.previousElementSibling;
      while (header && !header.classList.contains('submenu-header')) header = header.previousElementSibling;
      if (!header) return;
      const update = () => {
        const height = header.offsetHeight;
        if (height) this.style.setProperty('--os-mm-sticky-top', height + 'px');
      };
      update();
      if (window.ResizeObserver) {
        this.headerObserver = new ResizeObserver(update);
        this.headerObserver.observe(header);
      }
    }

    // Mobile: the tab and chip rows scroll sideways. data-os-mm-left (scrolled past the start) and
    // data-os-mm-end (at the end, or nothing to scroll) tell the CSS which edge to fade.
    // The drawer is hidden (width 0) at load, so the rows are measured again when their size changes.
    watchRows() {
      const rows = Array.from(this.querySelectorAll('.os-mmm__tabs, .os-mmm__chips'));
      if (!rows.length) return;
      const update = (row) => {
        const left = row.scrollLeft;
        row.toggleAttribute('data-os-mm-left', left > 2);
        row.toggleAttribute('data-os-mm-end', left + row.clientWidth >= row.scrollWidth - 2);
      };
      if (!this.rowsBound) {
        this.rowsBound = true;
        rows.forEach((row) => row.addEventListener('scroll', () => update(row), { passive: true }));
      }
      rows.forEach(update);
      if (window.ResizeObserver && !this.rowObserver) {
        this.rowObserver = new ResizeObserver((entries) => entries.forEach((entry) => update(entry.target)));
        rows.forEach((row) => this.rowObserver.observe(row));
      }
    }

    // Mobile: once the drawer scrolls, data-os-mm-scrolled lifts the pinned search and tabs with a shadow
    watchScroll() {
      const scroller = this.parentElement;
      if (!scroller || this.scroller === scroller) return;
      this.scroller = scroller;
      this.onScroll = () => this.toggleAttribute('data-os-mm-scrolled', scroller.scrollTop > 0);
      scroller.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }

    // Mobile: after a tab switch while scrolled down (the pinned bar overlapping the panel area), scroll
    // back so the new panel starts just under the pinned search and tabs instead of part way down.
    alignBody() {
      const top = this.querySelector('[data-os-mm-top]');
      const body = this.querySelector('[data-os-mm-body]');
      const scroller = this.parentElement;
      if (!top || !body || !scroller) return;
      const overlap = top.getBoundingClientRect().bottom - body.getBoundingClientRect().top;
      if (overlap > 1) scroller.scrollTop -= overlap;
    }

    // Desktop: run apply(), which changes what the panel area shows, and glide the panel area from its
    // old height to the new one. Panels take their own height, so the strip below moves with them.
    resize(apply) {
      const box = this.dataset.variant === 'desktop' ? this.querySelector('.os-mm__panels') : null;
      if (!box) {
        apply();
        return;
      }
      clearTimeout(this.resizeTimer);
      const from = box.getBoundingClientRect().height;
      box.style.height = '';
      box.classList.remove('is-resizing');
      apply();
      const to = box.getBoundingClientRect().height;
      if (reducedMotion() || Math.abs(to - from) < 1) return;
      box.style.height = from + 'px';
      void box.offsetHeight;
      box.classList.add('is-resizing');
      box.style.height = to + 'px';
      this.resizeTimer = setTimeout(() => {
        box.style.height = '';
        box.classList.remove('is-resizing');
      }, RESIZE_MS);
    }

    panelFor(tab) {
      const id = tab.getAttribute('aria-controls');
      if (!id) return null;
      return this.querySelector('#' + (window.CSS && CSS.escape ? CSS.escape(id) : id));
    }

    // Old School: when the visible panel is a wide one (more than 10 tiles), the shell drops the
    // campaign column so the tiles get the full width (the CSS also does this with :has()).
    markWide() {
      const shell = this.querySelector('.os-mm__shell');
      if (!shell) return;
      const wide = !!this.querySelector('.os-mm__panel[data-os-mm-wide]:not([hidden])');
      shell.toggleAttribute('data-os-mm-wide-active', wide);
    }

    select(tab, focus) {
      if (!tab) return;
      this.resize(() => {
        this.tabs.forEach((other) => {
          const active = other === tab;
          other.setAttribute('aria-selected', active ? 'true' : 'false');
          other.tabIndex = active ? 0 : -1;
          const panel = this.panelFor(other);
          if (!panel) return;
          if (active) warmImages(panel);
          panel.hidden = !active;
        });
        this.markWide();
      });
      if (focus) tab.focus({ preventScroll: true });
      if (this.dataset.variant === 'mobile') {
        this.revealTab(tab);
        this.alignBody();
      }
    }

    // Mobile tabs sit in a sideways scrolling row: centre the chosen one, so its neighbours show as
    // part pills under the edge fade
    revealTab(tab) {
      const list = tab.parentElement;
      if (!list || list.scrollWidth <= list.clientWidth) return;
      list.scrollTo({
        left: tab.offsetLeft - (list.clientWidth - tab.offsetWidth) / 2,
        behavior: reducedMotion() ? 'auto' : 'smooth',
      });
    }

    onTabKeydown(event, tab) {
      const index = this.tabs.indexOf(tab);
      const last = this.tabs.length - 1;
      let next = null;
      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          next = this.tabs[index === last ? 0 : index + 1];
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          next = this.tabs[index === 0 ? last : index - 1];
          break;
        case 'Home':
          next = this.tabs[0];
          break;
        case 'End':
          next = this.tabs[last];
          break;
        default:
          return;
      }
      event.preventDefault();
      this.select(next, true);
    }

    filter(chip) {
      const group = chip.closest('[data-os-mm-chips]');
      const panel = chip.closest('[data-os-mm-panel]');
      if (!group || !panel) return;
      const sport = chip.getAttribute('data-os-mm-chip');
      group.querySelectorAll('[data-os-mm-chip]').forEach((other) => {
        other.setAttribute('aria-pressed', other === chip ? 'true' : 'false');
      });
      // Old School (Global): a chip half under the row's edge fade scrolls fully into view
      const row = chip.closest('.os-mmm__chips');
      if (row && row.scrollWidth > row.clientWidth) {
        const r = row.getBoundingClientRect();
        const c = chip.getBoundingClientRect();
        row.scrollBy({ left: c.left + c.width / 2 - (r.left + r.width / 2), behavior: 'smooth' });
      }
      this.resize(() => {
        // A sport shows all its teams; "All" shows every tile again, or 13 and the "More teams" tile
        // when the panel has one
        panel.toggleAttribute('data-os-mm-expanded', sport !== 'all');
        panel.querySelectorAll('[data-os-mm-more]').forEach((more) => {
          more.setAttribute('aria-expanded', sport !== 'all' ? 'true' : 'false');
        });
        // data-os-sport lists every sport a team is in, space separated (e.g. "rugby football")
        panel.querySelectorAll('[data-os-sport]').forEach((item) => {
          const sports = (item.getAttribute('data-os-sport') || '').split(/\s+/);
          item.hidden = !(sport === 'all' || sports.includes(sport));
        });
      });
    }

    // Desktop: show every tile in the panel and move focus to the first one that was hidden
    expand(more) {
      const panel = more.closest('[data-os-mm-panel]');
      if (!panel) return;
      warmImages(panel);
      this.resize(() => panel.setAttribute('data-os-mm-expanded', ''));
      more.setAttribute('aria-expanded', 'true');
      const first = panel.querySelector('.os-mm__item--extra:not([hidden]) a');
      if (first) first.focus({ preventScroll: true });
    }

    showSlide(dot) {
      const card = dot.closest('[data-os-mm-campaign]');
      if (!card) return;
      const index = dot.getAttribute('data-os-mm-dot');
      card.querySelectorAll('[data-os-mm-slide]').forEach((slide) => {
        slide.hidden = slide.getAttribute('data-os-mm-slide') !== index;
      });
      card.querySelectorAll('[data-os-mm-dot]').forEach((other) => {
        other.setAttribute('aria-pressed', other === dot ? 'true' : 'false');
      });
    }
  }

  window.customElements.define('os-mega-menu', OsMegaMenu);

  // Old School: keep the mobile drawer's bottom bar (location, login) visible under the SHOP panel.
  // The CSS stops the panel above the bar; this measures the bar's real height whenever the drawer opens.
  function measureDock() {
    const drawer = document.querySelector('menu-drawer');
    const bar = drawer && drawer.querySelector('.header-menu-bottom');
    if (!bar || !bar.offsetHeight) return;
    drawer.style.setProperty('--os-mm-dock', bar.offsetHeight + 'px');
  }
  document.addEventListener('click', () => window.requestAnimationFrame(measureDock), true);
  window.addEventListener('resize', measureDock);
})();
